import type {ChangeEvent} from 'react';
import React from 'react'
import _ from 'lodash'
import type {
    OptionTuple,
    OptionTuples} from '../helpers/react-helpers';
import {
    lazyAsyncProp,
    lazyPropMulti,
    lazyPropSingle,
    selectOptions,
    updateState
} from '../helpers/react-helpers'
import Step from '../components/Step'
import RadioGroup from './RadioGroup'
import type {RadioButtonProps} from './RadioButton';
import RadioButton from '../components/RadioButton'
import Slider from '../components/Slider'
import TextBox from './TextBox'
import NumberBox from './NumberBox'
import SelectBox from './SelectBox'
import Segment from './Segment'
import * as kb from "../kb"
import {strlen, toBool} from "../kb"
import type { QueryParams} from '../helpers/ajax';
import {ajax, postJson, resolveRoute, responseToErrorMessage} from '../helpers/ajax'
import RDS from 'react-dom/server'
import type {AnySegment, SegmentData} from '../models/Segment';
import SegmentModel from '../models/Segment'
import type {InvoiceItemTemplateData} from '../models/InvoiceItemTemplate';
import InvoiceItemTemplate from '../models/InvoiceItemTemplate'
import type {InvoiceItemModel} from '../models/InvoiceItem';
import InvoiceItem from '../models/InvoiceItem'
import type {BookingStopData} from '../models/Stop';
import StopModel from '../models/Stop'
import Icon from './Icon'
import icons from '../../markons/markons'
import Modal from './Modal'
import ActionLink from './ActionLink'
import ActionButton from './ActionButton'
import ButtonRow from './ButtonRow'
// const $script = require('scriptjs');
// import Select from 'react-select';
import CustomerForm from './CustomerForm'
import GoogleMap from './GoogleMap'
import calculateDistance, {convertDistance} from '../distance'
import {formatSegments} from '../google-maps'
import type { AsyncPaginateProps as AsyncPaginatePropsTmpl} from 'react-select-async-paginate';
import {AsyncPaginate as ReactSelect} from 'react-select-async-paginate'
import type {OptionProps as ReactSelectOptionPropsTmpl} from 'react-select'
// import {GridCol, GridRow} from './grid-form';
import LoadingDots from './LoadingDots'
import CheckboxLabel from './CheckboxLabel'
import ForceScroll from './ForceScroll'
import ContentSection from './ContentSection'
import ContentIndent from "./ContentIndent"
import {GridCol, GridFormSingleSection, GridRow, GridRowSingleCol, HelpText, HtmlGridForm} from './grid-form'
import AioSelect from './aio-inputs/AioSelect'
import AioText from './aio-inputs/AioText'
import AioDollar from './aio-inputs/AioDollar'
import AioTextArea from './aio-inputs/AioTextArea'
import {FlexFixed, FlexGrow, FlexRow} from './Flex'
import type {Zone} from '../helpers/zone-types'
import {getCurrencyIcon} from '../data/currency-map'
import groupBookingCss from './GroupBookingDialog.less'
import {notifyError} from '../notify'
import AioPhoneNumber from './aio-inputs/AioPhoneNumber'
import {CancelLink, SaveButton} from './SaveSubmitButton'
import {captureSentryError} from '../sentry'
import BodyEnd from './BodyEnd'
import cn from 'classnames'
import setIn from 'lodash/fp/set'
import type {Spec} from 'immutability-helper2';
import update from 'immutability-helper2'
import TopoSort from 'topo-sort'
import {uuid} from '../util/random'
import {mapDefined} from '../util/collections'
import {
    clamp,
    divQR,
    floatFix,
    parseFloat,
    parseInt,
    round,
    roundNearest
} from '../util/math'
import AioRadio from './aio-inputs/AioRadio'
import AioCheckbox from './aio-inputs/AioCheckbox'

type Path = string | number | Array<string | number>

interface Package {
    id: number
    description: string
    feeIds: number[]
    name: string
    vehiclePrices: Array<{
        price: string
        vehicle_id: number
    }>
}

interface Vehicle {
    capacity: number
    code: string
    distance_base_price: number
    distance_rates: DistanceRate[]
    hourly_rate: number
    is_affiliate: number
    min_minutes: number
    max_rate: number
    min_rate: number
    name: string
}

interface InvoiceItemInterface {
    amount: number  // FIXME(#767): it's a string on page load, number after editing fees
    appliesToFeeIds: number[]
    appliesToIIIds: number[]
    appliesToIIKeys: string[]
    default: boolean
    percentOfBase: boolean
    feeId: number
    id: number | null
    key: string
    name: string
    quantity: number | null
    type: string
}

interface InvoiceItemWithIndex extends InvoiceItemInterface {
    idx: number
}

interface Position {
    id: null | number
    user_id: null | number
    position_id: null | number
    comment: string
    key?: string
}

interface BaseItem {
    name: string
    amount: number
}

interface FeeItem {
    key: string
    name: string
    amount: number
    quantity: number | null
    type: "PERCENT" | "FIXED"
    percentOfBase: boolean
    idx: number
}

const EMPTY_POSITION = kb.freeze<Position>({
    id: null,
    user_id: null,
    position_id: null,
    comment: '',
})

const SELECT2_MIN_LENGTH_SEARCH = 2

/**
 * Customer data for "Returning Customer" autocomplete field
 */
interface S2Customer {
    id: number;
    first_name: string;
    last_name: string;
    pri_phone_number: string;
    pri_phone_type: string;
    sec_phone_number: string;
    sec_phone_type: string;
    email: string;
    company: string;
}

interface ExtendedS2Customer extends S2Customer {
    // TODO: we shouldn't pass the entire customer record into JS, just the fields we need (above)
    number: number;
    fax_number?: string;
    company_id: number;
    deleted_at?: AnyDuringMigration;
    self_entered: number;
    stripe_customer_id: string;
    create_date_utc?: AnyDuringMigration;
    create_user_id?: string | number;
    notes?: string;
    mail_city: string;
    mail_postal_code: string;
    mail_street: string;
    mail_province: string;
    mail_place_name: string;
}

interface BillingSegment {
    billingType: string | null
    billedVehicleId: number
    vehicleId: number
    hourlyRate: number
    hours: number
    flatRate: number
    distance: number
}

interface ConflictSegment {
    segmentId: number | null
    vehicleId: number | null
    driverId: number | null
    date: string | null
    time: string | null
    number: number
    duration: number | null
}

interface LoadFormData {
    id?: string | number
    bill_total: number
    billing_type: string
    company_alias_id: number
    company_id: number
    customer_id: number | ''
    driver_notes: string
    flat_rate: string
    hours_override: boolean
    internal_notes: string
    invoice_items: InvoiceItemInterface[]
    is_confirmed: boolean
    is_deleted: boolean
    notes: string
    number: number
    package_description: string
    passenger_copy: string
    passenger_count: '' | number
    passenger_first_name: string
    passenger_last_name: string
    passenger_pri_phone_number: string
    passenger_pri_phone_type: string
    passenger_sec_phone_number: string
    passenger_sec_phone_type: string
    s2customer: ExtendedS2Customer
    segments: SegmentData[]
    single_vehicle_booking: StringBool
    different_billed_scheduled_vehicles: StringBool
    will_farm_out: StringBool
    status: string
    timezone: string
    total_hours: number
    total_mins: number
    positions: Position[]
    repeat_customer: 'NEW' | 'EXIST'
    package_id: NullablePhpId
    /** Scheduled Vehicle (affects conflict checking) */
    vehicle_id: NullablePhpId
    /** Billed Vehicle (affects billing) */
    billed_vehicle_id: NullablePhpId
    gnet_vehicle_type: string|null
    driver_id: NullablePhpId
    hourly_rate: NullablePhpId
}

type BlockedDateConflict = {
    vehicleDateBlocked: boolean,
    userDateBlocked: boolean,
    segNum: StringNum,
    reason: string,

    dateRange?: string
    vehicleName?: string,
    vehicleCode?: string,
    driverName?: string,
}

type BookingConflict = {
    bookingId: number,
    segmentId: number,
    bookingNumber: number,
    confirmedDate: string,
    pickupTime: string,
    dropoffTime: string,
    driverId: StringNum,
    vehicleId: StringNum,
    driverFirstName: string | null,
    driverLastName: string | null,
    vehicleName: string | null,
    vehicleCode: string | null,
    inputSegmentNumber: StringNum,
    driverMatches: boolean,
    vehicleMatches: boolean,
}

type AnyConflict = XOR<BlockedDateConflict | BookingConflict>

type LiveFormData = Override<LoadFormData, {
    segments: AnySegment[]
}>

type UpdateFormDataSpec = Spec<LiveFormData>

interface BookingFormState {
    data: LiveFormData
    isSaving: boolean
    /** @deprecated */
    custFetching?: boolean
    isDeleting?: boolean
    isCancelling?: boolean
    checkingConflicts?: boolean
    showAddInvoiceItemDialog: boolean
    showEditCustomerDialog: boolean
    conflicts?: AnyConflict[]
    newInvoiceItem?: InvoiceItemTemplateData
    /** @deprecated */
    custData?: S2Customer[]
}

interface Fee {
    id: number;
    name: string;
    type: string;
    amount: string;
    default: boolean;
    percentOfBase: boolean;
    appliesToFeeIds: number[];
}

interface Package {
    id: number;
    name: string;
    description: string;
    feeIds: number[];
    vehiclePrices: VehiclePrice[];
}

interface VehiclePrice {
    vehicle_id: number;
    price: string;
}

type DistanceRate = {
    up_to: number | null;
    rate: number;
}

interface BookingFormProps {
    data: LoadFormData
    vehicleSelect: OptionTuples
    positionUserOptions: OptionTuples
    positionJobOptions: OptionTuples
    gnetVehicleOptions: OptionTuples
    vehicleData: Record<number, Vehicle>
    packages: Package[]
    drivers: OptionTuples
    fees: Fee[]
    occasions: OptionTuples
    aliases: OptionTuples
    phoneTypes: OptionTuples
    countries: OptionTuples
    /** @deprecated */
    customerSelect2Url: string
    saveBookingUrl: string
    cancelBookingUrl: string
    googleMapsApiKey: string
    distanceUnit: string
    distanceRounding: string
    distanceNearest: number
    militaryTime: boolean
    zoneBillingEnabled: boolean
    zones: Zone[]
    currencyCode: string
}


function extractAddress(stop: BookingStopData) {
    return {
        city: stop.city,
        placeName: stop.place_name,
        postalCode: stop.postal_code,
        province: stop.province,
        street: stop.street,
    }
}

type ZoneAddress = ReturnType<typeof extractAddress>

interface ZoneSegment {
    dropoff: ZoneAddress
    pickup: ZoneAddress
    vehicleId: null | number
}

interface ZonePricing {
    totalFormatted: string;
    totalValue: number;
    segments: Array<ZonePricingSegmentOK | ZonePricingSegmentError>;
    errors: number;
}

interface ZonePricingSegmentOK {
    status: 'OK';
    priceValue: number;
    pickupZone: string;
    dropoffZone: string;
}

interface ZonePricingSegmentError {
    status: 'error';
    message: string;
    segment: number;
    address: ZonePricingAddress;
}

interface ZonePricingAddress {
    city: string;
    placeName: string;
    postalCode: string;
    province: string;
    street: string;
}

interface HourlyRateSliderOptions {
    min?: number
    max?: number
}

type ReactSelectAdditional = {
    pageOffset: number
}
type ReactSelectProps = AsyncPaginatePropsTmpl<S2Customer, never, ReactSelectAdditional, false>
type ReactSelectOptionProps = ReactSelectOptionPropsTmpl<S2Customer, false, never>

const reactSelectStyle: ReactSelectProps['styles'] = {
    menu: base => ({
        ...base,
        marginTop: 1,
        marginBottom: 0,
        // borderTopLeftRadius: 0,
        // borderTopRightRadius: 0,
    })
}

const NEW_OR_RETURNING_CUSTOMER_BUTTONS: RadioButtonProps[] = [
    {value: 'NEW', children: "New Customer", icon: icons.ionPersonAdd},
    {value: 'EXIST', children: "Returning Customer", icon: icons.ionRefresh},
]

const CUSTOMER_IS_PASSENGER_BUTTON: RadioButtonProps[] = [
    {value: 'SAME', children: "Yes, customer is passenger", icon: icons.miscCheck},
    {value: 'DIFFERENT', children: "No, specify other", icon: icons.miscCross},
]

function addressFilled(stop: any) {
    return Boolean(stop.street && ((stop.city && stop.province) || stop.postalCode))
}

export default class BookingForm extends React.Component<BookingFormProps, BookingFormState> {
    // Defined via lazyProps
    vehicleLookup!: Record<string, any>
    isSingle!: boolean
    singleBillingType!: string | null
    singleDriverId!: number | null
    singleFlatRate!: number | null
    singleVehicleId!: number | null
    billedVehicleId!: number | null
    bookingId!: number | null
    companyId!: number | null
    package!: Record<string, any> | undefined
    billedVehicleData!: Vehicle | null
    formData!: LiveFormData
    hoursOverride!: boolean
    lastConflictsPromise!: any
    phoneTypeMap!: any
    invoiceItemIds!: any[]
    invoiceLookup!: Record<string, any>
    baseAmount!: number
    feeItems!: FeeItem[]
    totalTripLength!: number
    feeAmount!: number
    singleHourlyRate!: number
    totalTripHoursMins!: [hours: number, mins: number]
    grandTotal!: number
    submitInvoiceItems!: any[]
    conflictSegments!: ConflictSegment[]
    conflicts!: Promise<unknown[]>
    baseItems!: BaseItem[]
    packages!: Package[]
    segments!: SegmentData[]
    billingSegments!: BillingSegment[]
    zoneSegments!: ZoneSegment[]
    zonePricing!: null | ZonePricing

    constructor(props: BookingFormProps) {
        super(props)
        this.state = {
            data: props.data,
            isSaving: false,
            showAddInvoiceItemDialog: false,
            showEditCustomerDialog: false,
        }
        // if(!props.data.id) {
        //     // Don't add positions if the feature isn't enabled!
        //     this.state.data.positions = [{...EMPTY_POSITION,key:kb.id()}]
        // }
        // this.state.data.segments = kb.addReactKeys(props.data.segments);
        this.state.data.segments = props.data.segments.map(seg => new SegmentModel({
            ...seg,
            stops: seg.stops ? seg.stops.map((stop: any) => new StopModel(stop)) : [],
            flight: seg.flight || Object.create(null),
        }))

        const invIdToKey: Record<number, string> = {}
        const feeIdToKey: Record<number, string> = {}
        for(const ii of this.state.data.invoice_items) {
            ii.key = uuid()
            if(ii.id) {
                invIdToKey[ii.id] = ii.key
            }
            if(ii.feeId) {
                feeIdToKey[ii.feeId] = ii.key
            }
        }

        for(const ii of this.state.data.invoice_items) {
            ii.appliesToIIKeys = ii.appliesToIIIds.map((id: any) => invIdToKey[id]).filter((x: any) => x)
                .concat(ii.appliesToFeeIds.map((id: any) => feeIdToKey[id]).filter((x: any) => x))
        }
        this.phoneTypeMap = _.fromPairs(props.phoneTypes)

        lazyPropMulti.call(this, 'billedVehicleId', [s => s.data.billed_vehicle_id], (vid: StringNum) => parseInt(vid))

        lazyPropMulti.call(this, 'singleVehicleId', [s => s.data.vehicle_id, 'billedVehicleId', s => toBool(s.data.different_billed_scheduled_vehicles,false), s => toBool(s.data.will_farm_out,false)], (svid, bvid, dbsv, wfo) => {
            if(wfo) {
                return null
            }
            if(!dbsv) {
                return bvid
            }
            return parseInt(svid)
        })

        lazyPropMulti.call(this, 'packages', ['billedVehicleId'], (vehicleId: StringNum) => this.props.packages.filter((pkg: any) => pkg.vehiclePrices.some((vp: any) => vp.vehicle_id == vehicleId)))

        lazyPropMulti.call(this, 'package', ['billedVehicleId', s => s.data.package_id], (vehicleId, packageId) => {
            const pkg = this.props.packages.find(pkg => pkg.id == packageId)

            if(pkg) {
                const vp = pkg.vehiclePrices.find(vp => vp.vehicle_id == vehicleId)
                if(vp) {
                    return {
                        packageId: pkg.id,
                        name: pkg.name,
                        price: vp.price,
                        vehicleId: vehicleId,
                        description: pkg.description,
                    }
                }
            }

            return null
        })

        lazyPropSingle.call(this, 'bookingId', (s, p) => s.data.id || p.data.id || null)

        lazyPropSingle.call(this, 'companyId', (s: BookingFormState, p: BookingFormProps) => s.data.company_id || p.data.company_id || null)

        lazyPropSingle.call(this, 'formData', s => s.data || {})

        lazyPropMulti.call(this, 'segments', ['isSingle', s => s.data.segments, 'singleVehicleId', 'singleDriverId', s => toBool(s.data.different_billed_scheduled_vehicles,false), s => toBool(s.data.will_farm_out,false)], (isSingle, segments, singleVehicleId, singleDriverId, differentBilledScheduledVehicles, willFarmOut) => segments.map((seg: SegmentData) => {
                if(isSingle) {
                    return {
                        ...seg,
                        driver_id: singleDriverId,
                        vehicle_id: singleVehicleId,
                    }
                }

                return {
                    ...seg,
                    driver_id: willFarmOut ? null : parseInt(seg.driver_id),
                    vehicle_id: willFarmOut ? null : parseInt(differentBilledScheduledVehicles ? seg.vehicle_id : seg.billed_vehicle_id),
                }
            })
        )

        lazyPropMulti.call(this, 'billingSegments', ['segments'], (segments) => segments.map((seg: SegmentData) => ({
            billingType: seg.billing_type || null,
            billedVehicleId: parseInt(seg.billed_vehicle_id, 0),
            vehicleId: parseInt(seg.vehicle_id || seg.billed_vehicle_id, 0),
            hourlyRate: parseFloat(seg.hourly_rate, 0),
            hours: parseFloat(seg.duration, 0) / 60,
            flatRate: parseFloat(seg.flat_rate, 0),
            distance: parseFloat(seg.distance, 0)
        })))

        lazyPropSingle.call(this, 'vehicleLookup', (s, p) => p.vehicleData)

        lazyPropMulti.call(this, 'isSingle', [s => s.data.single_vehicle_booking, s => s.data.segments.length, s => s.data.will_farm_out], (svb, len, wfo) => kb.toBool(svb) || len === 1 || toBool(wfo))

        lazyPropSingle.call(this, 'singleFlatRate', s => parseFloat(s.data.flat_rate, 0))
        lazyPropSingle.call(this, 'singleBillingType', s => s.data.billing_type || null)
        lazyPropMulti.call(this, 'singleDriverId', [s => s.data.driver_id, s => s.data.will_farm_out], (did,wfo) => toBool(wfo) ? null : parseInt(did))
        lazyPropMulti.call(this, 'billedVehicleData', ['billedVehicleId', (s, p) => p.vehicleData], (bvid, data) => bvid ? data[bvid] : null)
        lazyPropMulti.call(this, 'singleHourlyRate', [s => s.data.hourly_rate, 'billedVehicleData'], (rate, data) => rate && data ? clamp(rate, data.min_rate, data.max_rate) : 0)
        lazyPropMulti.call(this, 'hoursOverride', [s => s.data.hours_override, 'isSingle'], (override, single) => kb.toBool(override) && single)


        lazyPropMulti.call(this, 'totalTripHoursMins', [s => s.data.total_hours, s => s.data.total_mins, 'segments', 'hoursOverride'], (totalHours, totalMins, segments, hoursOverride) => {
            let totalTripMins, totalTripHours

            if(hoursOverride) {
                totalTripHours = parseFloat(totalHours, 0)
                totalTripMins = parseFloat(totalMins, 0)
            } else {
                totalTripHours = 0
                totalTripMins = _.sumBy(segments, seg => parseFloat(seg.duration, 0))

                if(totalTripMins >= 60) {
                    const [q, r] = divQR(totalTripMins, 60)
                    totalTripHours += q
                    totalTripMins = r
                }
            }

            return [totalTripHours, totalTripMins]
        })

        lazyPropMulti.call(this, 'totalTripLengthMins', ['totalTripHoursMins'], ([totalTripHours, totalTripMins]) => round(totalTripHours * 60 + totalTripMins))

        lazyPropMulti.call(this, 'totalTripLength', ['totalTripHoursMins'], ([totalTripHours, totalTripMins]) => round(totalTripHours + totalTripMins / 60, 2))

        lazyPropMulti.call(this, 'zoneSegments', ['segments'], (segments: SegmentData[]) => {
            const out = []
            for(const seg of segments) {
                if(seg.stops.length >= 2) {
                    const pickup = extractAddress(seg.stops[0])
                    if(!addressFilled(pickup)) continue
                    const dropoff = extractAddress(seg.stops[seg.stops.length - 1])
                    if(!addressFilled(dropoff)) continue
                    out.push({
                        vehicleId: seg.billed_vehicle_id || seg.vehicle_id || null,
                        pickup, dropoff
                    })
                }
            }
            return out
        })

        lazyAsyncProp.call(this, 'zonePricing', ['singleBillingType', 'zoneSegments'], async (singleBillingType: string, segments: ZoneSegment[]) => {
            if(!props.zoneBillingEnabled || singleBillingType !== 'ZONE') return null
            if(!segments.length) return null
            if(segments.some(seg => !seg.vehicleId)) return null
            return postJson(resolveRoute('api.zonePricing')!, {segments}).then(res => {
                if(res.ok) {
                    return res.data
                }
                captureSentryError(`Failed to fetch zone pricing for booking`,null, {
                        route: 'api.zonePricing',
                        message: responseToErrorMessage(res),
                        data: res.data,
                        status: res.status,
                },'d542e217-47c4-4de8-9ee8-748fef10df51')
                return null
            })
        })


        lazyPropMulti.call(this, 'baseItems', ['singleBillingType', 'singleFlatRate', 'billedVehicleId', 'isSingle', 'singleHourlyRate', 'totalTripLength', 'package', 'billingSegments', 'vehicleLookup', (s, p) => p.distanceNearest, (s, p) => p.distanceRounding, (s, p) => p.distanceUnit, 'zonePricing', 'zoneSegments', (s, p) => p.currencyCode, 'billedVehicleData', 'totalTripLengthMins'], (singleBillingType, singleFlatRate, billedVehicleId, isSingle, singleHourlyRate, totalTripLength, pkg, segments: BillingSegment[], vehicleLookup, distanceNearest, distanceRounding, distanceUnit, zonePricing: ZonePricing, zoneSegments: ZoneSegment[], currencyCode: string, billedVehicleData: Vehicle | null, totalTripLengthMins: number) => {

            const baseAmounts: BaseItem[] = []

            if(isSingle) {
                switch(singleBillingType) {
                    case 'HOURLY': {
                        let billedTripLengthMins = totalTripLengthMins
                        const underMin = Boolean(billedVehicleData?.min_minutes && billedTripLengthMins < billedVehicleData.min_minutes)
                        if(underMin) {
                            billedTripLengthMins = billedVehicleData!.min_minutes
                        }
                        baseAmounts.push({
                            name: `Base: ${kb.formatCurrencyIntl(singleHourlyRate, currencyCode)}/hr × ${kb.formatMinutes(billedTripLengthMins)}${underMin ? ' (minimum)' : ''}`,
                            amount: round(singleHourlyRate * billedTripLengthMins / 60, 2),
                            // key: 'single-hourly',
                        })
                    }
                        break
                    case 'FLAT':
                        baseAmounts.push({
                            name: "Flat Rate",
                            amount: round(singleFlatRate, 2),
                            // key: 'single-flat',
                        })
                        break
                    case 'PACKAGE':
                        if(pkg) {
                            baseAmounts.push({
                                name: `Package: ${pkg.name}`,
                                amount: round(pkg.price, 2),
                                // key: 'single-package',
                            })
                        }
                        break
                    case 'DISTANCE_TOTAL': {
                        let totalDist = 0
                        for(const seg of segments) {
                            totalDist += parseFloat(seg.distance, 0)
                        }
                        const roundedDist = roundNearest(totalDist, distanceNearest, distanceRounding)
                        const distanceRates = billedVehicleData?.distance_rates ?? []

                        let cost = billedVehicleData?.distance_base_price ?? 0
                        let distTrav = 0
                        for(const dr of distanceRates) {
                            if(dr.up_to && roundedDist >= dr.up_to) {
                                const partDist = dr.up_to - distTrav
                                cost += partDist * dr.rate
                                distTrav = dr.up_to
                            } else {
                                const partDist = roundedDist - distTrav
                                cost += partDist * dr.rate
                                break
                            }
                        }
                        baseAmounts.push({
                            name: `Base: ${floatFix(roundedDist)} ${distanceUnit}`,
                            amount: round(cost, 2),
                        })
                    }
                        break
                    case 'ZONE':
                        if(zonePricing) {
                            for(const seg of zonePricing.segments) {
                                if(seg.status === 'OK') {
                                    baseAmounts.push({
                                        name: `${seg.pickupZone} to ${seg.dropoffZone}`,
                                        amount: seg.priceValue,
                                    })
                                } else {
                                    baseAmounts.push({
                                        name: seg.message,
                                        amount: 0,
                                    })
                                }
                            }
                        } else {
                            let n = 1
                            for(const seg of zoneSegments) {
                                baseAmounts.push({
                                    name: `S${n}: ` + (seg.vehicleId ? "Error" : "Choose a Vehicle"),
                                    amount: 0,
                                })
                                ++n
                            }
                        }
                        break
                }
            } else {
                segments.forEach((seg, i) => {
                    switch(seg.billingType) {
                        case 'HOURLY': {
                            const segAmount = seg.hourlyRate * seg.hours
                            baseAmounts.push({
                                name: `Segment ${i + 1}: ${kb.formatCurrencyIntl(seg.hourlyRate, currencyCode)} × ${kb.formatHours(seg.hours)}`,
                                amount: round(segAmount, 2),
                                // key: `hourly-${i}`,
                            })
                            break
                        }
                        case 'FLAT': {
                            baseAmounts.push({
                                name: `Segment ${i + 1}: Flat Rate`,
                                amount: round(seg.flatRate, 2),
                                // key: `flat-${i}`,
                            })
                            break
                        }
                    }
                })
            }

            segments.forEach((seg, i) => {
                const segNbr = i + 1
                const billingType = isSingle ? singleBillingType : seg.billingType
                const vehicleId = isSingle ? billedVehicleId : seg.billedVehicleId
                const vehicle = _.get(vehicleLookup, vehicleId, null)

                if(billingType === 'DISTANCE') {
                    let dist = parseFloat(seg.distance, 0)
                    dist = roundNearest(dist, distanceNearest, distanceRounding)

                    let segCost = 0

                    if(vehicle) {
                        segCost += vehicle.distance_base_price

                        let distTrav = 0

                        for(const dr of vehicle.distance_rates) {
                            if(dr.up_to && dist >= dr.up_to) {
                                const partDist = dr.up_to - distTrav
                                segCost += partDist * dr.rate
                                distTrav = dr.up_to
                            } else {
                                const partDist = dist - distTrav
                                segCost += partDist * dr.rate
                                break
                            }
                        }
                    }

                    baseAmounts.push({
                        name: `Segment ${segNbr}: ${floatFix(dist)} ${distanceUnit}`,
                        amount: round(segCost, 2),
                        // key: `seg-distance-${i}`,
                    })
                } else if(!isSingle && billingType === 'DISTANCE_TOTAL') {
                    // throw new Error("DISTANCE_TOTAL not implemented for multi-vehicle bookings")
                    baseAmounts.push({
                        name: `Segment ${segNbr}: Cannot calculate using Distance (Total)`,
                        amount: 0,
                    })
                }
            })

            return baseAmounts
        })

        lazyPropMulti.call(this, 'baseAmount', ['baseItems'], (items: BaseItem[]) => items.length ? _.sumBy(items, ii => ii.amount) : 0)

        lazyPropMulti.call(this, 'invoiceItemIds', [(s: BookingFormState) => s.data.invoice_items], (invoiceItems: InvoiceItemInterface[]) => {
            const iiSort = new TopoSort()

            for(const ii of invoiceItems) {
                iiSort.add(ii.key, ii.appliesToIIKeys)
            }

            try {
                return iiSort.sort().reverse()
            } catch(err) {
                // console.error("Issue #737", err)
                notifyError(`Error in Fees & Discounts configuration. Please check the "Applies to" section of the following fees and ensure there are no cycles:${invoiceItems.map(ii => `\n- ${ii.name}`).join('')}`)
                return []
            }
        })

        lazyPropMulti.call(this, 'invoiceLookup', [(s: BookingFormState) => s.data.invoice_items], (invoiceItems: InvoiceItemInterface[]) => {
            const invoicePairs = invoiceItems.map((ii, idx) => [ii.key, {...ii, idx}])
            return _.fromPairs(invoicePairs)
        })

        lazyPropMulti.call(this, 'feeItems', ['invoiceItemIds', 'invoiceLookup', 'baseAmount'], (invoiceItemIds: string[], invoiceLookup: Record<string, InvoiceItemWithIndex>, baseAmount: number) => {
            // console.log('calc fees');
            const feeItems: FeeItem[] = []
            const amountLookup: Record<string, number> = {}
            for(const invKey of invoiceItemIds) {
                let amount: number
                let name: string
                let quantity: number | null
                const ii = invoiceLookup[invKey]

                if(!ii) {
                    console.info('invoiceLookup', JSON.stringify(invoiceLookup, null, 2))
                    console.info('invoiceItemIds', JSON.stringify(invoiceItemIds, null, 2))
                    throw new Error(`Could not find invoice item key '${invKey}' in lookup object`)
                }
                // console.log(ii);
                switch(ii.type) {
                    case 'PERCENT':
                        amount = 0
                        if(ii.percentOfBase == null || ii.percentOfBase) {
                            amount += ii.amount * baseAmount
                        }
                        name = `${ii.name} (${kb.formatFeePercent(ii.amount)})`
                        quantity = null
                        for(const applyKey of ii.appliesToIIKeys) {
                            if(!Object.hasOwn(amountLookup, applyKey)) {
                                throw new Error(`Amount for invoice item ${applyKey} was not calculated before ${invKey}`)
                            }
                            amount += ii.amount * amountLookup[applyKey]
                        }
                        break
                    case 'FIXED':
                        quantity = ii.quantity == null ? 1 : ii.quantity
                        amount = ii.amount * quantity!
                        name = ii.name
                        break
                    default:
                        throw new Error(`Unrecognized invoice item type "${ii.type}" for #${invKey}`)
                }
                // console.log(amount,name,kb.round(amount,2));
                amount = round(amount, 2)
                amountLookup[ii.key] = amount
                feeItems.push({
                    key: ii.key,
                    name: name,
                    amount: amount,
                    quantity,
                    type: ii.type,
                    percentOfBase: ii.percentOfBase,
                    idx: ii.idx,
                })
            }
            return feeItems
        })

        lazyPropMulti.call(this, 'feeAmount', ['feeItems'], (items: FeeItem[]) => items.length ? _.sumBy(items, ii => ii.amount) : 0)
        lazyPropMulti.call(this, 'grandTotal', ['feeAmount', 'baseAmount'], (feeAmount: any, baseAmount: any) => feeAmount + baseAmount)

        lazyPropMulti.call(this, 'submitInvoiceItems', ['baseItems', 'feeItems'], (baseItems: BaseItem[], feeItems: FeeItem[]) => [
            ...baseItems.map(({
                name,
                amount
            }: any) => ({name, amount: amount.toFixed(2), type: 'BASE'})),
            ...feeItems.map(({
                name,
                amount,
                quantity
            }: any) => {
                if(quantity != null && quantity != 1) name += ` × ${quantity}`
                return {name, amount: amount.toFixed(2), type: 'FEE'}
            }),
        ])

        lazyPropMulti.call(this, 'conflictSegments', ['segments'], (segments: SegmentData[]) => {
            const conflictSegments = segments.map((seg, i) => ({
                segmentId: seg.id ? parseInt(seg.id!) : null,
                vehicleId: parseInt(seg.vehicle_id),
                driverId: parseInt(seg.driver_id),
                date: seg.date || null,
                time: seg.time || null,
                number: i + 1,
                duration: seg.duration ? parseInt(seg.duration, 0) : null,
            }))

            // logJson(conflictSegments)

            const filteredSegments = conflictSegments.filter(s => s.date && s.time && s.duration && (s.vehicleId || s.driverId))

            return filteredSegments
        })

        lazyPropMulti.call(this, 'conflicts', ['bookingId', 'conflictSegments'], (bookingId: any, segments: any) => {
            if(!segments?.length) {
                // console.log('no segs 4 conflict checking')
                return Promise.resolve({
                    ok: true,
                    data: [],
                })
            }
            // console.log('yoyoyo');
            return postJson(resolveRoute('api.bookingConflicts')!, {bookingId, segments})
        })
    }

    componentDidMount() {
        this.updateConflicts()
    }

    componentDidUpdate() {
        this.updateConflicts()
    }

    updateConflicts() {
        const conflictsPromise = this.conflicts
        // console.log('updateConflicts',JSON.stringify(this.segments,null,2));
        if(this.lastConflictsPromise !== conflictsPromise) {
            this.setState({checkingConflicts: true})
            this.lastConflictsPromise = conflictsPromise
            conflictsPromise.then((res: any) => {
                // console.log('response',res.data)
                this.setState({checkingConflicts: false})
                if(res.ok) {
                    this.setState({conflicts: res.data})
                }
            })
        }
    }

    CustomerOption: React.FC<ReactSelectOptionProps> = ({innerProps, isDisabled, data, isSelected, isFocused}) => {
        const lines = []

        const name = `${data.first_name} ${data.last_name}`.trim()
        if(name) {
            lines.push(<div key="name" className="name">{name}</div>)
        }

        if(data.company) {
            lines.push(<div key="company" className="company">{data.company}</div>)
        }

        if(data.pri_phone_number) {
            lines.push(<div key="pri_phone"
                className="pri_phone">{data.pri_phone_number} ({this.phoneTypeMap[data.pri_phone_type]})</div>)
        }

        if(data.email) {
            lines.push(<div key="email" className="email">{data.email}</div>)
        }

        // let label
        // if(name) {
        //     label = name
        //     if(data.company) {
        //         label += `, ${data.company}`
        //     }
        // } else if(data.company) {
        //     label = data.company
        // } else {
        //     label = "(No Name)"
        // }

        return (
            <div {...innerProps}>
                <div className={cn({
                    "select2-customer-result": true,
                    'select2-customer-selected': isSelected,
                    'select2-customer-focused': isFocused,
                    'select2-customer-disabled': isDisabled,
                    'select2-customer-normal': !isSelected && !isFocused && !isDisabled
                })}>{lines}</div>
            </div>
        )
    }

    getCustomerOptionLabel: ReactSelectProps['getOptionLabel'] = data => {
        const name = `${data.first_name} ${data.last_name}`.trim()
        let label
        if(name) {
            label = name
            if(data.company) {
                label += `, ${data.company}`
            }
        } else if(data.company) {
            label = data.company
        } else {
            label = "(No Name)"
        }
        return label
    }

    customerOnChange: ReactSelectProps['onChange'] = (newValue, actionMeta) => {
        this.updateData({
            s2customer: {$set: newValue},
            customer_id: {$set: newValue?.id || ''},
        })
    }

    noOptionsMessage: ReactSelectProps['noOptionsMessage'] = ({inputValue}) => {
        if(strlen(inputValue) < SELECT2_MIN_LENGTH_SEARCH) {
            return `Search input must be at least ${SELECT2_MIN_LENGTH_SEARCH} characters`
        }
        return `No customers found`
    }

    loadCustomerOptions: ReactSelectProps['loadOptions'] = async (inputValue, options, additional) => {
        if(strlen(inputValue) < SELECT2_MIN_LENGTH_SEARCH) {
            return {
                options: [],
                hasMore: false,
            }
        }

        const pageOffset = additional?.pageOffset ?? 0

        const queryParams: QueryParams = {
            term: inputValue,
            pageOffset,
        }

        if(this.companyId) {
            queryParams.companyId = this.companyId
        }

        return ajax<{
            results: S2Customer[],
            more: boolean,
            pageOffset: number
        }>(this.props.customerSelect2Url || resolveRoute('customers.select2'), {
            method: 'GET',
            queryParams,
        }).then(res => {
            if(!res.ok) {
                throw new Error("Error fetching customer data")
            }

            return {
                options: res.data.results,
                hasMore: res.data.more,
                additional: {
                    pageOffset: pageOffset + 1,
                }
            }
        })
    }


    render() {
        const props = this.props
        const self = this
        const {vehicleSelect, drivers, occasions, phoneTypes, gnetVehicleOptions} = this.props
        const formData = this.formData
        const billedVehicleId = this.billedVehicleId
        const isHourly = formData.billing_type === 'HOURLY'
        const vehicleLookup = this.vehicleLookup
        const hourlyRateSliderOptions: HourlyRateSliderOptions = {}

        if(billedVehicleId && this.props.vehicleData[billedVehicleId]) {
            const billedVehicle = this.props.vehicleData[billedVehicleId]
            hourlyRateSliderOptions.min = billedVehicle.min_rate
            hourlyRateSliderOptions.max = billedVehicle.max_rate
        }

        const [totalTripHours, totalTripMins] = this.totalTripHoursMins
        const willFarmOut = toBool(formData.will_farm_out);
        const singleVehicleBooking = this.isSingle
        const differentBilledScheduledVehicles = toBool(formData.different_billed_scheduled_vehicles);
        const hoursOverride = this.hoursOverride
        const presetFees = this.props.fees.filter((ii: any) => ii.amount > 0)
        const presetDiscounts = this.props.fees.filter((ii: any) => ii.amount < 0)
        const googleMapSegments = formatSegments(formData.segments)
        const isEdit = !!this.props.data?.id

        let stepNumber = 0

        // console.log(googleMapSegments);

        return (
            <HtmlGridForm>
                <Step title="Customer Information" number={++stepNumber} grid>
                    <GridRow>
                        <GridCol>
                            <AioRadio {...this.linkData('repeat_customer')} testId="repeat-customer" label="New or returning customer?" buttons={NEW_OR_RETURNING_CUSTOMER_BUTTONS}/>
                        </GridCol>
                    </GridRow>

                    {formData.repeat_customer === 'NEW'
                        && <>
                            <GridRow>
                                <GridCol minWidth={180}>
                                    <AioText label="First Name" testId="new-customer-first-name"
                                        icon={icons.faUser} {...this.linkData('customer.first_name')} />
                                </GridCol>
                                <GridCol minWidth={180}>
                                    <AioText label="Last Name" testId="new-customer-last-name"
                                        icon={icons.faUser} {...this.linkData('customer.last_name')} />
                                </GridCol>
                            </GridRow>

                            <GridRow>
                                <GridCol minWidth={210}>
                                    <AioText label="Company" testId="new-customer-company-name"
                                        icon={icons.faBuilding} {...this.linkData('customer.company')} />
                                </GridCol>
                                <GridCol minWidth={210}>
                                    <AioText label="Email Address" testId="new-customer-email"
                                        icon={icons.faEnvelope} {...this.linkData('customer.email')} />
                                </GridCol>
                            </GridRow>

                            <GridRow>
                                <GridCol minWidth={225}>
                                    <AioPhoneNumber testId="new-customer-pri-phone"
                                        numberValue={this.getData('customer.pri_phone_number')}
                                        typeValue={this.getData('customer.pri_phone_type', 'CELL')}
                                        numberOnChange={this.makeSetData('customer.pri_phone_number')}
                                        typeOnChange={this.makeSetData('customer.pri_phone_type')}
                                        label="Primary Phone Number"
                                        typeOptions={this.props.phoneTypes} />
                                </GridCol>
                                <GridCol minWidth={225}>
                                    <AioPhoneNumber testId="new-customer-sec-phone"
                                        numberValue={this.getData('customer.sec_phone_number')}
                                        typeValue={this.getData('customer.sec_phone_type', 'HOME')}
                                        numberOnChange={this.makeSetData('customer.sec_phone_number')}
                                        typeOnChange={this.makeSetData('customer.sec_phone_type')}
                                        label="Secondary Phone Number"
                                        typeOptions={this.props.phoneTypes} />
                                </GridCol>
                            </GridRow>

                        </>
                    }

                    {formData.repeat_customer === 'EXIST'
                        &&
                        <GridRowSingleCol>
                            <ReactSelect<S2Customer, never, ReactSelectAdditional> loadOptions={this.loadCustomerOptions}
                                components={{Option: this.CustomerOption}}
                                placeholder="Type to search for customer..."
                                onChange={this.customerOnChange}
                                value={formData.s2customer}
                                getOptionValue={opt => String(opt.id)}
                                getOptionLabel={this.getCustomerOptionLabel}
                                noOptionsMessage={this.noOptionsMessage}
                                debounceTimeout={300}
                                styles={reactSelectStyle}
                            />
                            {formData.customer_id ? (
                                <div style={{marginTop: '4px'}}>
                                    <ActionLink onClick={this.editCustomer}>Edit Customer</ActionLink>
                                </div>
                            ) : null}
                        </GridRowSingleCol>
                    }
                </Step>

                <Step title="Passenger Information" number={++stepNumber} grid>
                    <div className="row">
                        <div className="col">
                            <AioRadio label="Is the customer also the passenger?" {...this.linkData('passenger_copy')} testId="passenger-copy" buttons={CUSTOMER_IS_PASSENGER_BUTTON}/>
                        </div>
                    </div>

                    {formData.passenger_copy === 'DIFFERENT'
                        && <>
                            <GridRow>
                                <div className="col" style={{minWidth: '180px'}}>
                                    <AioText label="First Name" testId="passenger-first-name"
                                        icon={icons.faUser} {...this.linkData('passenger_first_name')} />
                                </div>
                                <div className="col" style={{minWidth: '180px'}}>
                                    <AioText label="Last Name" testId="passenger-last-name"
                                        icon={icons.faUser} {...this.linkData('passenger_last_name')} />
                                </div>
                            </GridRow>
                            <GridRow>
                                <GridCol minWidth={225}>
                                    <AioPhoneNumber numberValue={this.getData('passenger_pri_phone_number')}
                                        typeValue={this.getData('passenger_pri_phone_type', 'CELL')}
                                        numberOnChange={this.makeSetData('passenger_pri_phone_number')}
                                        typeOnChange={this.makeSetData('passenger_pri_phone_type')}
                                        label="Primary Phone Number"
                                        typeOptions={this.props.phoneTypes}
                                        testId="passenger-pri-phone" />
                                </GridCol>
                                <GridCol minWidth={225}>
                                    <AioPhoneNumber numberValue={this.getData('passenger_sec_phone_number')}
                                        typeValue={this.getData('passenger_sec_phone_type', 'HOME')}
                                        numberOnChange={this.makeSetData('passenger_sec_phone_number')}
                                        typeOnChange={this.makeSetData('passenger_sec_phone_type')}
                                        label="Primary Phone Number"
                                        typeOptions={this.props.phoneTypes}
                                        testId="passenger-sec-phone" />
                                </GridCol>
                            </GridRow>
                            <GridRowSingleCol>
                                <AioText label="Email Address" testId="passenger-email"
                                    icon={icons.faEnvelope} {...this.linkData('passenger_email')} />
                            </GridRowSingleCol>
                        </>}
                </Step>

                <Step title="Trip Details" number={++stepNumber} grid>
                    <GridRow>
                        <GridCol minWidth="180px">
                            <AioSelect label="Trip Type" testId="trip-type"
                                icon={icons.miscMapMarker} {...this.linkData('trip_type', undefined, '')}
                                placeholder="— Not Selected —">
                                <option value="ONE_WAY">One Way</option>
                                <option value="ROUND_TRIP">Round Trip</option>
                                <option value="AS_DIRECTED">As Directed/Hourly</option>
                            </AioSelect>
                        </GridCol>

                        <div className="col" style={{minWidth: '180px'}}>
                            <AioSelect label="Occasion" testId="occasion"
                                icon={icons.faStar} {...this.linkData('occasion_id', undefined, '')}
                                options={occasions} placeholder="— Not Selected —" />
                        </div>

                        <div className="col" style={{minWidth: '140px'}}>
                            <AioSelect label="Total Passengers" testId="total-passengers"
                                icon={icons.faUserFriends} {...this.linkData('passenger_count', undefined, '')}
                                options={_.range(1, 201)} placeholder="— Not Selected —" />
                        </div>
                    </GridRow>
                </Step>
                <Step title="Trip Itinerary" number={++stepNumber}>
                    <div id="segment-container" className="segment-container">
                        {
                            _.get(formData, 'segments', []).map((seg: any, i: any) => {
                                return <Segment
                                    key={seg.key}
                                    {...this.linkData(`segments.${i}`, _.identity)}
                                    number={i + 1}
                                    addStop={this.addStop(i)}
                                    removeStop={this.removeStop(i)}
                                    removeSegment={this.removeSegment(i)}
                                    billingType={singleVehicleBooking ? formData.billing_type : seg.billing_type}
                                    distanceUnit={this.props.distanceUnit}
                                    googleMapsApiKey={this.props.googleMapsApiKey}
                                    militaryTime={this.props.militaryTime}
                                />
                            })
                        }
                    </div>

                    <div className="centered-row">
                        <div style={{minWidth: '283px'}}>
                            <ActionLink testId="add-segment"
                                className="btn btn-new left"
                                onClick={this.addSegment}><Icon
                                name={icons.ionAndroidAddCircle} /> Add Segment</ActionLink>
                            <ActionLink testId="add-reverse-route"
                                className="btn btn-new left"
                                onClick={this.addReverseRoute}><Icon
                                name={icons.faRetweet} /> Add Reverse Route</ActionLink>
                        </div>

                        {isHourly ?
                            <FlexRow gap={5}>
                                <label htmlFor="total-hours" style={{fontWeight: 'bold'}}>Total Trip Length</label>
                                <TextBox id="total-hours" className="duration-input" value={totalTripHours}
                                    type="number" onChange={this.changeData('total_hours')}
                                    disabled={!hoursOverride} />
                                <label htmlFor="total-hours"> Hr{totalTripHours == 1 ? '' : 's'} </label>
                                <TextBox id="total-mins" className="duration-input" value={totalTripMins} type="number"
                                    onChange={this.changeData('total_mins')} disabled={!hoursOverride} />
                                <label htmlFor="total-mins"> Min{totalTripMins == 1 ? '' : 's'}</label>

                                {singleVehicleBooking ?
                                    <CheckboxLabel style={{marginLeft: '5px'}} checked={formData.hours_override}
                                        onChange={this.hoursOverrideChecked}>Override</CheckboxLabel>
                                    : null}
                            </FlexRow> : null}
                    </div>

                </Step>

                <ContentSection title="Map" storageKey="create-booking-map">
                    <GoogleMap segments={googleMapSegments} apiKey={this.props.googleMapsApiKey}
                        distanceUnit={this.props.distanceUnit} zones={this.props.zones} />
                </ContentSection>

                <Step title="Vehicle & Driver" number={++stepNumber} grid>
                    {!isEdit && gnetVehicleOptions?.length ? <GridRowSingleCol>
                        <AioCheckbox label="Will Farm Out" {...this.linkDataChecked('will_farm_out')} />
                    </GridRowSingleCol> : null}
                    <GridRowSingleCol>
                        <AioCheckbox label="Different billed & scheduled vehicle" disabled={willFarmOut} {...this.linkDataChecked('different_billed_scheduled_vehicles')} />
                    </GridRowSingleCol>
                    <GridRowSingleCol>
                        <AioCheckbox label="Different vehicle, driver, and/or pricing for each segment" disabled={willFarmOut || formData.segments.length < 2} checked={!toBool(formData.single_vehicle_booking)} onChange={this.changeSingleVehicleBooking}/>
                    </GridRowSingleCol>

                    {singleVehicleBooking ? (
                        <>
                            <GridRow>
                                <GridCol minWidth={240}>
                                    <AioSelect icon={icons.faCar}
                                        label={differentBilledScheduledVehicles || willFarmOut ? "Billed Vehicle" : "Vehicle"}
                                        testId="billed-vehicle"
                                        value={formData.billed_vehicle_id || ''}
                                        onChange={this.billedVehicleChanged}
                                        options={vehicleSelect}>
                                        <option value="">— Vehicle TBD —</option>
                                    </AioSelect>
                                </GridCol>
                                {differentBilledScheduledVehicles && !willFarmOut ?
                                    <GridCol minWidth={240}>
                                        <AioSelect icon={icons.faCar} label="Scheduled Vehicle" testId="scheduled-vehicle"
                                            value={formData.vehicle_id || ''} onChange={this.vehicleChanged}
                                            options={vehicleSelect}>
                                            <option value="">— TBD / Affiliate Vehicle —</option>
                                        </AioSelect>
                                    </GridCol> : null}
                                {(willFarmOut || isEdit) && gnetVehicleOptions?.length ?
                                    <GridCol minWidth={240}>
                                        <AioSelect label="GNet Vehicle" options={gnetVehicleOptions} {...this.linkData('gnet_vehicle_type')}>
                                            <option value="">— Not Selected —</option>
                                        </AioSelect>
                                    </GridCol>
                                : null}
                                {!willFarmOut ?
                                    <GridCol minWidth={240}>
                                        <AioSelect icon={icons.faUserTie} testId="driver-id"
                                            label="Driver" value={formData.driver_id || ''} onChange={this.driverChanged}
                                            options={drivers}>
                                            <option value="">— TBD / Affiliate Driver —</option>
                                        </AioSelect>
                                    </GridCol>
                                : null}
                            </GridRow>
                        </>
                    ) : (
                        <div className="row">
                            <div className="col">
                                <table className="simple-table">
                                    <thead>
                                        <tr>
                                            <th style={{width: '4%'}}>Seg.</th>
                                            {differentBilledScheduledVehicles ? <>
                                                <th style={{width: '32%'}}>Billed Vehicle</th>
                                                <th style={{width: '32%'}}>Scheduled Vehicle</th>
                                                <th style={{width: '32%'}}>Driver</th>
                                            </> : <>
                                                <th style={{width: '60%'}}>Vehicle</th>
                                                <th style={{width: '40%'}}>Driver</th>
                                            </>}

                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            _.get(formData, 'segments', []).map((seg: any, i: any) => {
                                                return <tr key={seg.key}>
                                                    <td className="tcenter vcenter">S{i + 1}</td>
                                                    <td>
                                                        <SelectBox
                                                            value={self.getData(`segments.${i}.billed_vehicle_id`, '')}
                                                            onChange={self.multiBilledVehicleChanged(i)}>
                                                            <option value="">— Vehicle TBD —</option>
                                                            {selectOptions(vehicleSelect)}
                                                        </SelectBox>
                                                    </td>
                                                    {differentBilledScheduledVehicles ? <td>
                                                        <SelectBox value={self.getData(`segments.${i}.vehicle_id`, '')}
                                                            onChange={self.multiVehicleChanged(i)}>
                                                            <option value="">— TBD / Affiliate Vehicle —</option>
                                                            {selectOptions(vehicleSelect)}
                                                        </SelectBox>
                                                    </td>:null}

                                                    <td>
                                                        <SelectBox
                                                            id="driver_id" {...this.linkData(`segments.${i}.driver_id`, undefined, '')}>
                                                            <option key="" value="">— TBD / Affiliate Driver —</option>
                                                            {selectOptions(drivers)}
                                                        </SelectBox>
                                                    </td>
                                                </tr>
                                            })
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </Step>

                {this.props.positionJobOptions?.length ? <Step title="Other Positions" number={++stepNumber} grid>
                    {formData.positions.map((pos, idx) => (
                        <GridRow key={pos.id ?? pos.key}>
                            <GridCol flex={2} minWidth={210}>
                                <AioSelect label="Person"
                                    placeholder="— Not Selected —"
                                    options={this.props.positionUserOptions} {...this.linkData(`positions.${idx}.user_id`)} />
                            </GridCol>
                            <GridCol flex={2} minWidth={210}>
                                <AioSelect label="Job"
                                    placeholder="— Not Selected —"
                                    options={this.props.positionJobOptions} {...this.linkData(`positions.${idx}.position_id`)} />
                            </GridCol>
                            <GridCol flex={3} minWidth={210}>
                                <AioTextArea label="Comment" {...this.linkData(`positions.${idx}.comment`)} />
                            </GridCol>
                            <GridCol shrink center>
                                <ActionLink onClick={this.removePosition(idx)}
                                    className="mrkn med-icon fa-times-circle-hallow" />
                            </GridCol>
                        </GridRow>
                    ))}
                    <GridRow>
                        <GridCol>
                            <ActionButton className="btn-new"
                                onClick={this.addPosition}><Icon name={icons.ionAndroidAddCircle} /> Add
                                Position</ActionButton>
                            <HelpText>Note that there is no conflict checking on these positions. Please confirm that
                                everyone is available.</HelpText>
                        </GridCol>
                    </GridRow>
                </Step> : null}

                <Step
                    title={`Conflicts` + (this.state.conflicts && this.state.conflicts.length ? ` (${this.state.conflicts.length})` : '')}
                    storageKey="create-booking-conflicts">
                    {this.state.checkingConflicts ?
                        <div style={{textAlign: 'center', padding: '5px 0'}}><LoadingDots /></div> : null}
                    {this.state.conflicts && this.state.conflicts.length
                        ? (
                            <ul className="conflicts-list">
                                {this.state.conflicts.filter(c => c.driverMatches).map(conflict => <li
                                    key={`${conflict.inputSegmentNumber}-${conflict.segmentId}-driver`}>Driver {conflict.inputSegmentNumber}: <b>{conflict.driverFirstName} {conflict.driverLastName}</b> is
                                    already scheduled {conflict.pickupTime}&ndash;{conflict.dropoffTime} (<a
                                        href={resolveRoute('bookings.show', {id: conflict.bookingId})}
                                        target="_blank"
                                        rel="noreferrer">Booking
                                        #{conflict.bookingNumber} &ndash; {conflict.confirmedDate ? "CONFIRMED" : "QUOTE"}</a>)
                                </li>)}
                                {this.state.conflicts.filter(c => c.vehicleMatches).map(conflict => <li
                                    key={`${conflict.inputSegmentNumber}-${conflict.segmentId}-vehicle`}>Vehicle {conflict.inputSegmentNumber}: <b>{conflict.vehicleName} ({conflict.vehicleCode})</b> is
                                    already scheduled {conflict.pickupTime}&ndash;{conflict.dropoffTime} (<a
                                        href={resolveRoute('bookings.show', {id: conflict.bookingId})}
                                        target="_blank"
                                        rel="noreferrer">Booking
                                        #{conflict.bookingNumber} &ndash; {conflict.confirmedDate ? "CONFIRMED" : "QUOTE"}</a>)
                                </li>)}
                                {this.state.conflicts.filter(c => c.vehicleDateBlocked).map((conflict, idx) => (
                                    <li key={`vbd-${idx}`}>Segment {conflict.segNum}: <b>{conflict.vehicleName} ({conflict.vehicleCode})</b> is
                                        scheduled off {conflict.dateRange} due to <b>{conflict.reason}</b></li>
                                ))}
                                {this.state.conflicts.filter(c => c.userDateBlocked).map((conflict, idx) => (
                                    <li key={`vbd-${idx}`}>Segment {conflict.segNum}: <b>{conflict.driverName}</b> is
                                        scheduled off {conflict.dateRange} due to <b>{conflict.reason}</b></li>
                                ))}
                            </ul>
                        )
                        : (this.state.checkingConflicts ? null :
                            <p className="no-conflicts-message">No conflicts found.</p>)
                    }
                </Step>

                <Step title="Notes" number={++stepNumber} grid>
                    <GridRow>
                        <GridCol>
                            <AioTextArea testId="internal-notes"
                                label="Internal Notes" {...this.linkData('internal_notes')} />
                        </GridCol>
                    </GridRow>
                    <GridRow>
                        <GridCol>
                            <AioTextArea testId="driver-notes"
                                label="Driver Notes" {...this.linkData('driver_notes')} />
                        </GridCol>
                    </GridRow>
                    <GridRow>
                        <GridCol>
                            <AioTextArea testId="customer-notes" label="Customer Notes" {...this.linkData('notes')} />
                        </GridCol>
                    </GridRow>
                    {formData.billing_type === 'PACKAGE' ? (
                        <GridRow>
                            <GridCol>
                                <AioTextArea testId="package-description"
                                    label="Package Description" {...this.linkData('package_description')} />
                            </GridCol>
                        </GridRow>
                    ) : null}
                </Step>

                <Step title="Billing" number={++stepNumber} grid>
                        {toArr(function* () {
                            if(singleVehicleBooking) {
                                const billingTypes: RadioButtonProps[] = [
                                    {value: 'HOURLY', children: "Hourly", icon: icons.faClock},
                                    {value: 'FLAT', children: "Flat", icon: icons.faDollar},
                                    {value: 'PACKAGE', children: "Package", icon: icons.miscPackage},
                                ]
                                if(formData.segments.length > 1 || formData.billing_type === 'DISTANCE') {
                                    billingTypes.push({value: 'DISTANCE', children: "Distance (per Seg)", icon: icons.faRoute})
                                }
                                billingTypes.push({value: 'DISTANCE_TOTAL', children: "Distance (Total)", icon: icons.faRoute})
                                if(props.zoneBillingEnabled) {
                                    billingTypes.push({value: 'ZONE', children: "Zone", icon: icons.miscMapMarker})
                                }

                                yield (
                                    <GridRowSingleCol key="billing_type">
                                        <AioRadio label="Billing Type" onChange={self.singleBillingTypeChange}
                                            value={formData.billing_type} testId="billing-type" buttons={billingTypes}/>
                                    </GridRowSingleCol>
                                )

                                switch(formData.billing_type) {
                                    case 'HOURLY':
                                        yield (
                                            <GridRowSingleCol key="hourly_rate">
                                                <FlexRow gap={5}>
                                                    <FlexFixed width={120}>
                                                        <AioDollar label="Hourly Rate" {...self.linkData('hourly_rate')}
                                                            onBlur={self.clampHourlyRate}
                                                            disabled={!billedVehicleId}
                                                            currency={self.props.currencyCode} />
                                                    </FlexFixed>
                                                    <FlexGrow>
                                                        <Slider value={self.singleHourlyRate}
                                                            onSlide={(ev, ui) => self.setData('hourly_rate', ui.value.toFixed(2))} {...hourlyRateSliderOptions}
                                                            disabled={!billedVehicleId} />
                                                    </FlexGrow>
                                                </FlexRow>
                                            </GridRowSingleCol>
                                        )
                                        break
                                    case 'FLAT':
                                        yield (
                                            <GridRowSingleCol key="flat_rate">
                                                <AioDollar testId="flat-rate" width={120}
                                                    label="Flat Rate" {...self.linkData('flat_rate')}
                                                    currency={self.props.currencyCode} />
                                            </GridRowSingleCol>
                                        )
                                        break
                                    case 'PACKAGE':
                                        yield (
                                            <GridRowSingleCol key="package">
                                                {self.packages.length > 0
                                                    ?
                                                    <AioSelect testId="package" label="Package" icon={icons.miscPackage}
                                                        onChange={self.packageChange}
                                                        value={formData.package_id}
                                                        placeholder="— Not Selected —"
                                                        options={self.packages.map(p => [p.id, p.name] as OptionTuple)} />
                                                    : <span>No packages available for selected vehicle.</span>}
                                            </GridRowSingleCol>
                                        )
                                        break
                                }
                            } else {
                                yield <GridRowSingleCol key="billing-type-table">
                                    <ForceScroll>
                                        <table className="simple-table">
                                            <thead>
                                                <tr>
                                                    <th className="nowrap">Segment</th>
                                                    <th className="nowrap">Billing Type</th>
                                                    <th className="fullwidth nowrap">Rate</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    _.get(formData, 'segments', []).map((seg: any, i: any) => {
                                                        const billedVehicleId = self.getData(`segments.${i}.billed_vehicle_id`, null)
                                                        const billedVehicle = _.get(vehicleLookup, billedVehicleId, {})

                                                        return (
                                                            <tr key={seg.key}>
                                                                <td className="nowrap vcenter tcenter">
                                                                    S{i + 1} ({_.get(billedVehicle, 'code', '--')})
                                                                </td>
                                                                <td className="nowrap" style={{minWidth: '180px'}}>
                                                                    {billedVehicleId
                                                                        ?
                                                                        <RadioGroup
                                                                            value={self.getData(`segments.${i}.billing_type`, '')}
                                                                            onChange={self.multiBillingTypeChanged(i)}>
                                                                            <RadioButton
                                                                                value="HOURLY">Hourly</RadioButton>
                                                                            <RadioButton value="FLAT">Flat</RadioButton>
                                                                            <RadioButton
                                                                                value="DISTANCE">Distance</RadioButton>
                                                                        </RadioGroup>
                                                                        : '--'
                                                                    }
                                                                </td>
                                                                <td className={formData.segments[i].billing_type === 'DISTANCE' || formData.segments[i].billing_type === 'DISTANCE_TOTAL' ? 'vcenter' : 'nowrap'}>
                                                                    {toArr(function* () {
                                                                        if(!billedVehicleId) {
                                                                            yield '--'
                                                                            return
                                                                        }
                                                                        switch(formData.segments[i].billing_type) {
                                                                            case 'HOURLY':
                                                                                yield (
                                                                                    <div
                                                                                        key="hourly_rate_textbox_container"
                                                                                        className="textbox-and-slider">
                                                                                        <div className="tas-textbox">
                                                                                            $<TextBox id="hourly_rate"
                                                                                            onBlur={self.clampHourlyRateSeg(i)}
                                                                                            {...self.linkData(`segments.${i}.hourly_rate`)}
                                                                                            className="amount-input"
                                                                                            disabled={!billedVehicleId} />/hour
                                                                                        </div>
                                                                                        <div className="tas-slider">
                                                                                            <Slider
                                                                                                value={self.getData(`segments.${i}.hourly_rate`)}
                                                                                                onSlide={(ev, ui) => self.setData(`segments.${i}.hourly_rate`, ui.value.toFixed(2))}
                                                                                                min={billedVehicle.min_rate}
                                                                                                max={billedVehicle.max_rate} />
                                                                                        </div>
                                                                                    </div>
                                                                                )
                                                                                break
                                                                            case 'FLAT':
                                                                                yield <div
                                                                                    key="flat_rate_container">$<TextBox {...self.linkData(`segments.${i}.flat_rate`)}
                                                                                    className="amount-input" />
                                                                                </div>
                                                                                break
                                                                            case 'DISTANCE':
                                                                                yield <span key="not_selected">Calculated automatically from <a
                                                                                    href={resolveRoute('vehicles.edit', {id: billedVehicleId})}
                                                                                    target="_blank" rel="noreferrer">distance-based pricing</a>.</span>
                                                                                break
                                                                            case 'DISTANCE_TOTAL':
                                                                                yield <span key="not_selected">Please select a Billing Type.</span>
                                                                                break;
                                                                            default:
                                                                                yield <span
                                                                                    key="not_selected">--</span>
                                                                                break
                                                                        }
                                                                    })}
                                                                </td>
                                                            </tr>
                                                        )
                                                    })
                                                }
                                            </tbody>
                                        </table>
                                    </ForceScroll>
                                </GridRowSingleCol>
                            }
                        })}
                    <div className="row">
                        <div className="col">
                            <table className="billing-table">
                                <tbody>
                                    {this.baseItems.map((bi, idx) => {
                                        return <tr key={idx}>
                                            <td className="name"><strong>{bi.name}</strong></td>
                                            <td className="amount">{kb.formatCurrencyIntl(bi.amount, this.props.currencyCode)}</td>
                                            <td className="emptyCell" />
                                        </tr>
                                    })}

                                    {this.feeItems.map((ii) => {
                                        return (<tr key={ii.key}>
                                            <td className="name">
                                                <div className="space-between">
                                                    <span>{ii.name}</span>
                                                    {ii.type === 'FIXED' ? <span>&times;{' '}
                                                        <NumberBox {...this.linkData(`invoice_items.${ii.idx}.quantity`)}
                                                            width={80} min={1} step={1} /></span> : null}
                                                </div>
                                            </td>
                                            <td className="amount">{kb.formatCurrencyIntl(ii.amount, this.props.currencyCode)}</td>
                                            <td className="actions">
                                                <a className="mrkn ion-trash-a delete-link"
                                                    onClick={this.removeInvoiceItem(ii.key)} />
                                            </td>
                                        </tr>)
                                    })}

                                    <tr>
                                        <td colSpan={2} className="tcenter"><ActionLink
                                            onClick={this.addInvoiceItem}><Icon name={icons.ionAndroidAddCircle}
                                            color="#45C054" /> Add Item</ActionLink>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td className="name"><strong>Grand Total</strong></td>
                                        <td className="amount">
                                            <strong>{kb.formatCurrencyIntl(this.grandTotal, this.props.currencyCode)}</strong>
                                        </td>
                                        <td className="emptyCell" />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Step>

                <Step title="Status & Alias" number={++stepNumber} grid>
                    <GridRow>
                        <GridCol minWidth={150}>
                            <AioSelect testId="booking-status" label="Booking Status" {...this.linkData('status')}>
                                <option key="quote" value="quote">Quote</option>
                                <option key="confirmed" value="confirmed">Confirmed</option>
                                {!!formData.id && <option key="cancelled" value="cancelled">Cancelled</option>}
                            </AioSelect>
                        </GridCol>
                        <GridCol minWidth={210}>
                            <AioSelect testId="company-alias" label="Company Alias" options={this.props.aliases}
                                placeholder="— Company Alias —" {...this.linkData('company_alias_id')} />
                        </GridCol>
                        <GridCol minWidth={175}>
                            <AioText testId="reference-number"
                                label="Reference #" {...this.linkData('reference_number')} />
                        </GridCol>
                    </GridRow>
                </Step>

                <ContentIndent>
                    <ButtonRow>
                        <ButtonRow.Left>
                            <SaveButton testId="save"
                                processing={this.state.isSaving}
                                icon={icons.miscSave}
                                onClick={this.saveBooking}>Save</SaveButton>
                        </ButtonRow.Left>
                        <ButtonRow.Right>
                            <CancelLink testId="discard-changes"
                                href={formData.id ? `/bookings/${formData.id}` : '/bookings'}>Discard
                                Changes</CancelLink>
                            {formData.id && !formData.is_deleted ?
                                <ActionButton testId="cancel-booking"
                                    type="button"
                                    className="btn-cancel"
                                    onClick={this.cancelBooking}><Icon
                                    name={icons.faBan} /> Cancel Booking</ActionButton> : null}
                            {formData.id && formData.is_deleted ?
                                <ActionButton testId="delete-booking"
                                    type="button"
                                    className="btn-cancel"
                                    onClick={this.deleteBooking}><Icon
                                    name={icons.ionTrashA} /> Permanently Delete</ActionButton> : null}
                        </ButtonRow.Right>
                    </ButtonRow>
                </ContentIndent>

                {!!this.state.showAddInvoiceItemDialog && (
                    <BodyEnd>
                        <Modal minWidth="300px" dialogClass={groupBookingCss.dialog}>
                            <div className={groupBookingCss.titleBar}>Fees & Discounts</div>
                            <div className={groupBookingCss.content}>
                                <GridFormSingleSection>
                                    <GridRow>
                                        <GridCol>
                                            <label>What would you like to add?</label>
                                            <RadioGroup {...this.linkState('newInvoiceItem.type')}>
                                                <RadioButton value="FEE">Fee</RadioButton>
                                                <RadioButton value="DISCOUNT">Discount</RadioButton>
                                            </RadioGroup>
                                        </GridCol>
                                    </GridRow>
                                    {presetFees.length && _.get(this.state, 'newInvoiceItem.type') === 'FEE' ?
                                        <GridRow>
                                            <GridCol>
                                                <label>Preset fees:</label>
                                                <ul className="preset-fee-list">
                                                    {presetFees.map((ii: any) => {
                                                        const format = ii.type === 'FIXED' ? kb.makeCurrencyFormatter(this.props.currencyCode) : kb.formatFeePercent
                                                        const name = `${ii.name} (${format(ii.amount)})`
                                                        return <li key={ii.id}>
                                                            <ActionLink onClick={this.presetFee(ii)}>{name}</ActionLink>
                                                        </li>
                                                    })}
                                                </ul>
                                            </GridCol>
                                        </GridRow> : null
                                    }
                                    {presetDiscounts.length && _.get(this.state, 'newInvoiceItem.type') === 'DISCOUNT' ?
                                        <GridRow>
                                            <GridCol>
                                                <label>Preset discounts:</label>
                                                <ul className="preset-fee-list">
                                                    {presetDiscounts.map((ii: any) => {
                                                        const format = ii.type === 'FIXED' ? kb.makeCurrencyFormatter(this.props.currencyCode) : kb.formatFeePercent
                                                        const name = `${ii.name} (${format(Math.abs(ii.amount))})`
                                                        return <li key={ii.id}><ActionLink
                                                            onClick={this.presetFee(ii)}>{name}</ActionLink></li>
                                                    })}
                                                </ul>
                                            </GridCol>
                                        </GridRow> : null
                                    }
                                    <GridRow>
                                        <GridCol>
                                            <AioText label="Name" {...this.linkState('newInvoiceItem.name')} />
                                        </GridCol>
                                    </GridRow>
                                    <GridRow>
                                        <GridCol>
                                            <label>Percent or fixed amount?</label>
                                            <RadioGroup {...this.linkState('newInvoiceItem.rateType')}>
                                                <RadioButton value="PERCENT">Percent</RadioButton>
                                                <RadioButton value="FIXED">Fixed</RadioButton>
                                            </RadioGroup>
                                        </GridCol>
                                        <GridCol>
                                            <AioText label="Amount" inputMode="decimal"
                                                step={0.5} {...this.linkState('newInvoiceItem.amount')}
                                                disabled={!_.get(this.state, 'newInvoiceItem.rateType')}
                                                icon={_.get(this.state, 'newInvoiceItem.rateType') === 'PERCENT' ? icons.faPercent : getCurrencyIcon(this.props.currencyCode)}
                                                width={100} />
                                        </GridCol>

                                    </GridRow>
                                    <GridRow>
                                        {_.get(this.state, 'newInvoiceItem.type') ?
                                            <GridCol>
                                                <label>Applies to this:</label>
                                                <ul className="checkbox-list">
                                                    {(() => {
                                                        let invoiceItems = formData.invoice_items.filter((ii: any) => ii.type === 'PERCENT')
                                                        if(_.get(this.state, 'newInvoiceItem.type') === 'DISCOUNT') {
                                                            invoiceItems = invoiceItems.filter((ii: any) => ii.amount > 0) // doesn't make sense to give discounts on discounts
                                                        }
                                                        return invoiceItems.map((ii: any) => {
                                                            return <li key={ii.key}><CheckboxLabel
                                                                checked={this.state.newInvoiceItem && this.state.newInvoiceItem.appliesToThis.has(ii.key)}
                                                                onChange={this.appliesToThisChange(ii)}>{ii.name}</CheckboxLabel>
                                                            </li>
                                                        })
                                                    })()}
                                                </ul>
                                            </GridCol> : null}
                                        {_.get(this.state, 'newInvoiceItem.rateType') === 'PERCENT' &&
                                            <GridCol>
                                                <label>Percentage of:</label>
                                                <ul className="checkbox-list">
                                                    <li>
                                                        <CheckboxLabel {...this.linkStateChecked('newInvoiceItem.percentOfBase')}>Base
                                                            Amount</CheckboxLabel>
                                                    </li>
                                                    {(() => {
                                                        let invoiceItems = formData.invoice_items
                                                        if(_.get(this.state, 'newInvoiceItem.type') === 'DISCOUNT') {
                                                            invoiceItems = invoiceItems.filter((ii: any) => ii.amount > 0) // doesn't make sense to give discounts on discounts
                                                        }
                                                        return invoiceItems.map((ii: any) => {
                                                            return <li key={ii.key}>
                                                                <CheckboxLabel
                                                                    checked={this.state.newInvoiceItem && this.state.newInvoiceItem.percentageOf.has(ii.key)}
                                                                    onChange={this.percentageOfChange(ii)}>{ii.name}</CheckboxLabel>
                                                            </li>
                                                        })
                                                    })()}
                                                </ul>
                                            </GridCol>
                                        }
                                    </GridRow>
                                </GridFormSingleSection>
                                <ButtonRow>
                                    <ButtonRow.Left>
                                        <ActionButton className="btn-save" onClick={this.addNewFee}
                                            disabled={!this.feeFormFilled.call(this)}><Icon
                                            name="ion-plus-round" /> Add</ActionButton>
                                    </ButtonRow.Left>
                                    <ButtonRow.Right>
                                        <ActionButton className="btn-default" onClick={this.closeAddFeeDialog}><Icon
                                            name="ion-backspace-outline" /> Cancel</ActionButton>
                                    </ButtonRow.Right>
                                </ButtonRow>
                            </div>
                        </Modal></BodyEnd>
                )}

                {!!this.state.showEditCustomerDialog && (
                    <BodyEnd>
                        <Modal minWidth="300px">
                            <p><strong>Warning:</strong> Customer changes will affect ALL of their past/future bookings.
                            </p>
                            <CustomerForm customerId={formData.customer_id} phoneTypes={phoneTypes}
                                afterSave={this.afterSaveCustomer} onCancel={this.cancelEditCustomer}
                                googleMapsApiKey={this.props.googleMapsApiKey} />
                        </Modal>
                    </BodyEnd>
                )}
            </HtmlGridForm>
        )
    }

    feeFormFilled() {
        return _.get(this.state, 'newInvoiceItem.type')
            && _.get(this.state, 'newInvoiceItem.amount')
            && _.get(this.state, 'newInvoiceItem.rateType')
            && _.get(this.state, 'newInvoiceItem.name')
    }

    addNewFee = (ev: any) => {
        const newInvItemTpl = this.state.newInvoiceItem!
        let amount = newInvItemTpl.amount ? Math.abs(parseFloat(newInvItemTpl.amount, 0)) : 0
        if(newInvItemTpl.rateType == 'PERCENT') {
            amount /= 100
        }
        if(newInvItemTpl.type == 'DISCOUNT') {
            amount = -amount
        }

        for(const iiKey of newInvItemTpl.appliesToThis) {
            if(newInvItemTpl.percentageOf.has(iiKey)) {
                notifyError(`The same fee cannot be selected for both "Applies to this" and "Percentage of"`)
                return
            }
        }

        this.setState(state => {
            const currentInvoiceItems = state.data.invoice_items

            const newInvItem = new InvoiceItem({
                amount: amount,
                name: newInvItemTpl.name,
                type: newInvItemTpl.rateType,
                feeId: newInvItemTpl.feeId,
                percentOfBase: newInvItemTpl.percentOfBase,
                appliesToFeeIds: newInvItemTpl.appliesToFeeIds,   // Issue #804
                appliesToIIKeys: Array.from(newInvItemTpl.percentageOf),
                appliesToIIIds: currentInvoiceItems.filter(ii => ii.id && newInvItemTpl.percentageOf.has(ii.key)).map(ii => ii.id!),
            })

            const invoiceItemUpdates = {
                $push: [newInvItem]
            }

            for(const [i, ii] of currentInvoiceItems.entries()) {
                if(newInvItemTpl.appliesToThis.has(ii.key)) {
                    invoiceItemUpdates[i] = {
                        appliesToIIKeys: {$push: [newInvItem.key]}
                    }
                }
            }

            // console.log(invoiceItemUpdates);

            return update(state, {
                data: {
                    invoice_items: invoiceItemUpdates
                },
                showAddInvoiceItemDialog: {$set: false},
            })
        })
    }

    presetFee = (fee: any) => (ev: any) => {
        const appliesToThis = this.state.data.invoice_items.filter(ii => _.includes(ii.appliesToFeeIds, fee.id)).map(ii => ii.key)
        const appliesToFeeIds = new Set(fee.appliesToFeeIds)
        const percentageOf = this.state.data.invoice_items.filter(ii => appliesToFeeIds.has(ii.feeId)).map(ii => ii.key)
        const absAmount = Math.abs(fee.amount)
        let amount: string
        if(fee.type === 'PERCENT') {
            amount = (absAmount * 100).toString()
        } else {
            amount = absAmount.toFixed(2)
        }

        this.setState({
            newInvoiceItem: update(this.state.newInvoiceItem, {
                name: {$set: fee.name},
                rateType: {$set: fee.type},
                type: {$set: fee.amount >= 0 ? 'FEE' : 'DISCOUNT'},
                amount: {$set: amount},
                appliesToThis: {$set: new Set(appliesToThis)},
                appliesToFeeIds: {$set: fee.appliesToFeeIds},  // Issue #804
                percentageOf: {$set: new Set(percentageOf)},
                feeId: {$set: fee.id},
                percentOfBase: {$set: fee.percentOfBase},
            }),
        })
    }

    appliesToThisChange = (ii: InvoiceItemModel) => (ev: InputChangeEvent) => {
        const appliesToThis = new Set(this.state.newInvoiceItem?.appliesToThis)
        const percentageOf = new Set(this.state.newInvoiceItem?.percentageOf)
        if(ev.target.checked) {
            appliesToThis.add(ii.key)
            percentageOf.delete(ii.key)
        } else {
            appliesToThis.delete(ii.key)
        }
        this.setState({
            newInvoiceItem: update(this.state.newInvoiceItem, {
                appliesToThis: {$set: appliesToThis},
                percentageOf: {$set: percentageOf},
            })
        })
    }

    percentageOfChange = (ii: InvoiceItemModel) => (ev: InputChangeEvent) => {
        const appliesToThis = new Set(this.state.newInvoiceItem?.appliesToThis)
        const percentageOf = new Set(this.state.newInvoiceItem?.percentageOf)
        if(ev.target.checked) {
            percentageOf.add(ii.key)
            appliesToThis.delete(ii.key)
        } else {
            percentageOf.delete(ii.key)
        }
        this.setState({
            newInvoiceItem: update(this.state.newInvoiceItem, {
                percentageOf: {$set: percentageOf},
                appliesToThis: {$set: appliesToThis},
            })
        })
    }

    addInvoiceItem = (ev: any) => {
        this.setState({
            showAddInvoiceItemDialog: true,
            newInvoiceItem: new InvoiceItemTemplate,
        })
    }

    closeAddFeeDialog = (ev: any) => {
        ev.preventDefault()
        this.setState({showAddInvoiceItemDialog: false})
    }

    calculateDistances = () => {
        const segments = _.get(this, 'state.data.segments', []) as SegmentData[]
        // console.log("CALC DISTANCES",segments);
        for(const [i, seg] of segments.entries()) {
            calculateDistance(seg.stops, this.props.googleMapsApiKey)
                .then(dist => {
                    this.setData(`segments.${i}.distance`, convertDistance(dist, this.props.distanceUnit))
                }, err => {
                    // don't care. user can press "Calc"
                })
        }

    }

    deleteBooking = (ev: any) => {
        if(!confirm(`Are you sure you want to PERMANENTLY DELETE this booking? You will not be able to recover it after it's gone.`)) {
            return
        }

        this.setState({isDeleting: true})

        postJson(resolveRoute('bookings.delete', {id: this.formData.id})!).then((res: any) => {
            if(res.ok) {
                if(res.data.redirect) {
                    kb.navigate(res.data.redirect)
                } else {
                    kb.navigate('/bookings')
                }
            } else {
                if(res.data && res.data.errors && !_.isEmpty(res.data.errors)) {
                    kb.notify({
                        html: RDS.renderToStaticMarkup(ValidationErrorMessage({errors: res.data.errors})),
                        className: "warning",
                    })
                } else if(res.status === 403) {
                    kb.notify({
                        html: "You do not have sufficient privileges to delete this booking.",
                        className: "error",
                    })
                } else if(res.status >= 500 && res.status < 600) {
                    kb.notify({
                        html: "Server error. This service may be down or broken at moment; please bear with us while we get it working again!",
                        className: "error",
                    })
                } else {
                    throw new Error('Unknown client error')
                }
                this.setState({isSaving: false})
            }
        }).catch((err: any) => {
            this.setState({isDeleting: false})
            kb.notify({
                html: "A network error occurred. Please try a few more times!",
                className: "error",
            })
            console.error('Save error', err)
        })

    }

    cancelBooking = (ev: any) => {
        this.setState({isCancelling: true})

        postJson(this.props.cancelBookingUrl, this.state.data).then((res: any) => {
            if(res.ok) {
                if(res.data.redirect) {
                    kb.navigate(res.data.redirect)
                } else {
                    kb.navigate('/bookings')
                }
            } else {
                if(res.data && res.data.errors && !_.isEmpty(res.data.errors)) {
                    kb.notify({
                        html: RDS.renderToStaticMarkup(ValidationErrorMessage({errors: res.data.errors})),
                        className: "warning",
                    })
                } else if(res.status === 403) {
                    kb.notify({
                        html: "You do not have sufficient privileges to cancel this booking.",
                        className: "error",
                    })
                } else if(res.status >= 500 && res.status < 600) {
                    kb.notify({
                        html: "Server error. This service may be down or broken at moment; please bear with us while we get it working again!",
                        className: "error",
                    })
                } else {
                    throw new Error('Unknown client error')
                }
                this.setState({isSaving: false})
            }
        }).catch((err: any) => {
            this.setState({isCancelling: false})
            kb.notify({
                html: "A network error occurred. Please try a few more times!",
                className: "error",
            })
            console.error('Save error', err)
        })

    }

    saveBooking = (ev: any) => {
        this.setState({isSaving: true})

        postJson(this.props.saveBookingUrl, {
            ...this.state.data,
            billing_items: this.submitInvoiceItems,
            grand_total: this.grandTotal.toFixed(2),
        }).then((res: any) => {
            if(res.ok) {
                if(res.data.redirect) {
                    kb.navigate(res.data.redirect)
                } else {
                    kb.notify({
                        html: "Booking saved!",
                        className: "success",
                    })

                    this.setState({isSaving: false})
                }
            } else {
                if(res.data && res.data.errors && !_.isEmpty(res.data.errors)) {
                    kb.notify({
                        html: RDS.renderToStaticMarkup(ValidationErrorMessage({errors: res.data.errors})),
                        className: "warning",
                    })
                } else if(res.status === 403) {
                    kb.notify({
                        html: "You do not have sufficient privileges to save this booking.",
                        className: "error",
                    })
                } else if(res.status >= 500 && res.status < 600) {
                    kb.notify({
                        html: "Server error. This service may be down or broken at moment; please bear with us while we get it working again!",
                        className: "error",
                    })
                } else {
                    throw new Error('Unknown client error')
                }
                this.setState({isSaving: false})
            }
        }).catch((err: any) => {
            this.setState({isSaving: false})
            kb.notify({
                html: "A network error occurred. Please try a few more times!",
                className: "error",
            })
            console.error('Save error', err)
        })
    }

    addSegment = (ev: any) => {
        ev.preventDefault()
        this.updateData({segments: {$push: [new SegmentModel()]}})
        // console.log(this.state.data.segments);
    }

    addPosition = (ev: any) => {
        ev.preventDefault()
        this.updateData({positions: {$push: [{...EMPTY_POSITION, key: kb.id()}]}})
    }

    removePosition = (idx: number) => (ev: any) => {
        this.updateData({positions: {$splice: [[idx, 1]]}})
    }

    addReverseRoute = (ev: any) => {
        ev.preventDefault()
        this.updateData({
            segments: {
                $apply: (segments: AnySegment[]) => [
                    ...segments,
                    ...segments.map(seg => new SegmentModel({
                        ...seg,
                        date: "",
                        time: "",
                        id: null,
                        stops: seg.stops.map((stop: any) => new StopModel({
                            ...stop,
                            id: null,
                        })).reverse(),
                    })).reverse()
                ]
            }
        })
        // console.log(this.state.data.segments);
    }

    addStop = (segIdx: any) => (stopIdx: any) => (ev: any) => {
        ev.preventDefault()
        // console.log('seg',segIdx,'stop',stopIdx);
        // if(!this.segments[segIdx].stops)
        this.updateData({segments: {[segIdx]: {stops: {$splice: [[stopIdx, 0, new StopModel()]]}}}})
    }

    removeSegment = (segIdx: any) => (ev: any) => {
        ev.preventDefault()

        this.setState(({data}) => {
            data = update(data, {segments: {$splice: [[segIdx, 1]]}})

            if(data.segments.length === 1 && !this.isSingle) {
                // If there's only 1 segment left, copy it into the single vehicle/driver fields so that it doesn't
                // get overwritten. See issue #972
                const seg = data.segments[0]
                data = update(data, {
                    driver_id: {$set: seg.driver_id},
                    billed_vehicle_id: {$set: seg.billed_vehicle_id},
                    vehicle_id: {$set: seg.vehicle_id},
                })
            }

            return {data}
        })
    }

    removeInvoiceItem = (feeKey: any) => (ev: any) => {
        ev.preventDefault()

        this.setState(state => {
            const iiIdx = _.findIndex(state.data.invoice_items, ii => ii.key === feeKey)

            return update(state, {
                data: {
                    invoice_items: {
                        $splice: [[iiIdx, 1]],
                    }
                }
            })
        })

        this.setState(state => {
            const updates: Spec<InvoiceItemInterface[]> = {}

            for(let i = 0; i < state.data.invoice_items.length; ++i) {
                const ii = state.data.invoice_items[i]
                const remIdx = ii.appliesToIIKeys.indexOf(feeKey)
                // console.log(ii.appliesToIIKeys,remIdx);
                if(remIdx >= 0) {
                    updates[i] = {appliesToIIKeys: {$splice: [[remIdx, 1]]}}
                }
            }
            // updates.$splice = [[feeIdx, 1]];
            // console.log(JSON.stringify(updates,null,2));

            return update(state, {
                data: {
                    invoice_items: updates
                }
            })
        })
    }

    removeStop = (segIdx: any) => (stopIdx: any) => (ev: any) => {  // f92ea2d2-a894-4802-995f-2b362f1f30d6
        ev.preventDefault()
        this.updateData({
            segments: {
                [segIdx]: {
                    stops: {
                        $apply: (stops: StopModel[]) => {
                            if(stops.length <= 2) {
                                // FIXME: better to clear the fields than to replace the stop with a new model.
                                // This changes the React key which causes it to re-mount and lose internal state.
                                // e.g. the "Turn Autocomplete on/off" state will be lost.
                                // However, we *do* want to clear the Google address state, and we can't do that
                                // externally at the moment.
                                // return imUpdate(stops, {[stopIdx]: {$merge: {
                                //             city: "",
                                //             postal_code: "",
                                //             street: "",
                                //             province: "",
                                //             place_name: "",
                                //         }}})
                                return update(stops, {[stopIdx]: {$set: new StopModel()}})
                            }
                            return update(stops, {$splice: [[stopIdx, 1]]})
                        }
                    }
                }
            }
        })
    }

    updateData = (commands: Spec<typeof this.state.data>) => {
        // see: https://legacy.reactjs.org/docs/update.html#update
        // https://github.com/kolodny/immutability-helper#update
        this.setState(({data}) => ({data: update(data, commands)}))
    }

    // updateDataCallback = (callback: ((data: typeof this.state.data) => Spec<typeof this.state.data>|null)) => {
    //     this.setState(({data}) => {
    //         const spec = callback(data)
    //         if(spec) {
    //             return {data: update(data, spec)}
    //         }
    //         return null
    //     })
    // }

    setData = (name: string, value: any, cb?: () => void) => {
        this.setState(setIn(`data.${name}`, value), cb)
    }

    makeSetData = (name: string) => (ev: CompatEvent) => this.setData(name, ev.target.value)

    getData = (name: string | string[], defaultValue: any = undefined) => {
        return _.get(this.state.data, name, defaultValue)
    }

    vehicleChanged = (ev: any) => {
        const vehicle_id = ev.target.value
        const updates: Spec<LiveFormData> = {
            vehicle_id: {$set: vehicle_id},
            segments: {
                $apply: (segments:AnySegment[]) => segments.map(seg => ({...seg, vehicle_id}))
            }
        }
        this.updateData(updates)
        // console.log('vehicle changed',ev.target.value);
    }

    billedVehicleChanged = (ev: any) => {
        const billed_vehicle_id = ev.target.value
        const updates: Spec<LiveFormData> = {
            billed_vehicle_id: {$set: billed_vehicle_id},
            hourly_rate: {
                $set: billed_vehicle_id && this.props.vehicleData[billed_vehicle_id]
                    ? this.props.vehicleData[billed_vehicle_id].hourly_rate
                    : ''
            },
            segments: {
                $apply: (segments:AnySegment[]) => segments.map(seg => ({...seg, billed_vehicle_id}))
            }
        }
        this.updateData(updates)
        // console.log('vehicle changed',ev.target.value);
    }

    driverChanged = (ev: any) => {
        const driver_id = ev.target.value
        const updates: Spec<LiveFormData> = {
            driver_id: {$set: driver_id},
            segments: {
                $apply: (segments:AnySegment[]) => segments.map(seg => ({...seg, driver_id}))
            }
        }
        this.updateData(updates)
        // console.log('driver changed',ev.target.value);
    }

    multiVehicleChanged = (segId: any) => (ev: any) => {
        this.setData(`segments.${segId}.vehicle_id`, ev.target.value, () => {
            this.setDefaultRate(segId)
        })
    }

    multiBilledVehicleChanged = (segId: any) => (ev: any) => {
        this.setData(`segments.${segId}.billed_vehicle_id`, ev.target.value, () => {
            this.setDefaultRate(segId)
        })
    }

    multiBillingTypeChanged = (segId: any) => (ev: any) => {
        // console.log('multiBillingTypeChanged',segId,ev.target.value);
        this.setData(`segments.${segId}.billing_type`, ev.target.value, () => {
            this.setDefaultRate(segId)
        })
    }

    setDefaultRate(segId: any) {
        const billedVehicleId = this.getData(`segments.${segId}.billed_vehicle_id`)
        const billingType = this.getData(`segments.${segId}.billing_type`)
        if(billedVehicleId && billingType === 'HOURLY') {
            this.setData(`segments.${segId}.hourly_rate`, _.get(this.props.vehicleData, [billedVehicleId, 'hourly_rate'], ''))
        }
    }

    linkState = (name: Path, valueGetter = (ev: any) => ev.target.value, defaultValue = '') => ({
        value: _.get(this.state, name, defaultValue),
        onChange: (...args: any[]) => {
            const value = valueGetter(...args)
            this.setState(setIn(name, value))
        },
    })

    linkStateChecked = (name: Path, defaultValue = false) => ({
        checked: kb.toBool(_.get(this.state, name, defaultValue)),
        onChange: (ev: ChangeEvent<HTMLInputElement>) => {
            this.setState(setIn(name, ev.target.checked))
        },
    })

    linkData = (name: string, valueGetter = (ev: any) => ev.target.value, defaultValue = '') => this.linkState(`data.${name}`, valueGetter, defaultValue)

    linkDataChecked = (name: string) => this.linkStateChecked(`data.${name}`)

    // linkChecked = (name: any, valueGetter = (ev: any) => ev.target.checked) => {
    //     let prop = `data.${name}`;
    //     return {
    //         checked: kb.toBool(_.get(this.state, prop, null)),
    //         onChange: (...args) => {
    //             let value = kb.toBool(valueGetter(...args));
    //             this.setState(setIn(prop, value))
    //         },
    //     };
    // };

    changeData = (name: any, valueGetter = (ev: any) => ev.target.value) => (...args) => {
        const value = valueGetter(...args)
        this.setState(setIn(`data.${name}`, value))
    }

    clampHourlyRate = () => {
        const billedVehicle = this.billedVehicleData
        if(billedVehicle) {
            this.updateData({
                hourly_rate: {
                    $apply: hourly_rate => {
                        const hourly_rate_float = parseFloat(hourly_rate) ?? 0
                        if(hourly_rate_float < billedVehicle.min_rate) {
                            return billedVehicle.min_rate
                        }
                        if(hourly_rate_float > billedVehicle.max_rate) {
                            return billedVehicle.max_rate
                        }
                        return hourly_rate_float.toFixed(2)
                    }
                }
            })
        }
    }

    clampHourlyRateSeg = (segId: any) => (ev: any) => {
        this.updateData({
            segments: {
                [segId]: {
                    $apply: seg => {
                        const billedVehicleId = parseInt(seg.billed_vehicle_id || seg.vehicle_id)
                        if(billedVehicleId == null) return seg
                        const hourlyRate = parseFloat(seg.hourly_rate) ?? 0
                        const vehicleData = this.props.vehicleData[billedVehicleId]

                        if(!vehicleData) {
                            captureSentryError(`Billed vehicle not found in this.props.vehicleData`,null, {
                                companyId: window.company?.id,
                                billedVehicleId,
                                vehicleData: this.props.vehicleData,
                            },'4981f7be-7ea1-4ef8-9459-0a37e9894eb2')
                            return seg
                        }

                        if(hourlyRate < vehicleData.min_rate) {
                            return {...seg, hourly_rate: vehicleData.min_rate}
                        }
                        if(hourlyRate > vehicleData.max_rate) {
                            return {...seg, hourly_rate: vehicleData.max_rate}
                        }
                        return {...seg, hourly_rate: hourlyRate.toFixed(2)}
                    }
                }
            }
        })
    }

    editCustomer = (ev: any) => {
        ev.preventDefault()
        this.setState({showEditCustomerDialog: true})
    }

    cancelEditCustomer = () => {
        this.setState({showEditCustomerDialog: false})
    }

    afterSaveCustomer = (customer: S2Customer) => {
        kb.notify({
            text: 'Customer details updated',
            className: 'success',
        })
        updateState.call(this, {
            showEditCustomerDialog: {$set: false},
            data: {
                s2customer: {$set: customer},
            }
        })
    }

    hoursOverrideChecked = (ev: any) => {
        const updates = {hours_override: {$set: ev.target.checked}}
        if(ev.target.checked) {
            let totalTripHours = 0
            let totalTripMins = _.sumBy(this.state.data.segments, seg => parseFloat(seg.duration))

            if(totalTripMins >= 60) {
                const [q, r] = divQR(totalTripMins, 60)
                totalTripHours += q
                totalTripMins = r
            }

            updates.total_hours = {$set: totalTripHours}
            updates.total_mins = {$set: totalTripMins}
        }
        this.updateData(updates)
    }

    singleBillingTypeChange = (ev: any) => {
        // https://bitbucket.org/kymark/kymark-bookings/issues/375
        const bvid = _.get(this.state, 'data.billed_vehicle_id')

        this.updateData({
            billing_type: {$set: ev.target.value},
            hourly_rate: {
                $set: bvid && this.props.vehicleData[bvid]
                    ? this.props.vehicleData[bvid].hourly_rate : ''
            }
        })

        if(ev.target.value === 'DISTANCE' || ev.target.value === 'DISTANCE_TOTAL') {
            this.calculateDistances()
        }
    }

    packageChange = (ev: any) => {
        const packageId = ev.target.value
        const pkg = this.packages.find((p: any) => p.id == packageId)
        if(!pkg) return
        // console.log('pkg',pkg);

        const fees = this.props.fees.filter(fee => pkg.feeIds.includes(fee.id))
        const invoiceItems: InvoiceItemInterface[] = fees.map(fee => ({
            amount: fee.amount,
            feeId: fee.id,
            key: uuid(),
            id: null,
            name: fee.name,
            type: fee.type,
            percentOfBase: fee.percentOfBase,
            quantity: 1,

            // not sure if needed
            default: fee.default,

            appliesToFeeIds: fee.appliesToFeeIds,
            appliesToIIIds: [],
            appliesToIIKeys: []
        }))

        const feeIdToKey = invoiceItems.reduce((acc: Record<number, string>, ii) => {
            acc[ii.feeId] = ii.key
            return acc
        }, {})

        invoiceItems.forEach(ii => {
            ii.appliesToIIKeys = mapDefined(ii.appliesToFeeIds, id => feeIdToKey[id])
        })

        const vp = pkg.vehiclePrices.find(vp => vp.vehicle_id == this.billedVehicleId)

        // console.log('PACKAGE',pkg);
        // console.log('feeIdToKey',feeIdToKey);
        // console.log('invoiceItems',invoiceItems);

        this.updateData({
            package_id: {$set: packageId},
            invoice_items: {$set: invoiceItems},
            package_description: {$set: pkg.description},
            flat_rate: {$set: vp ? vp.price : '0.00'},
            hourly_rate: {$set: null},
        })
    }

    changeSingleVehicleBooking = (ev: ChangeEvent<HTMLInputElement>) => {
        this.setData('single_vehicle_booking', !ev.target.checked)

        if(ev.target.checked && this.formData.billing_type === 'HOURLY' && this.formData.hourly_rate) {
            // Issue #439
            const singleHourlyRate = round(this.formData.hourly_rate, 2)
            for(let i = 0; i < this.formData.segments.length; ++i) {
                if(this.formData.segments[i].billing_type === 'HOURLY') {
                    let segHourlyRate = singleHourlyRate
                    const billedVehicleId = parseInt(this.getData(`segments.${i}.billed_vehicle_id`) || this.getData(`segments.${i}.vehicle_id`))
                    const vehicleData = this.props.vehicleData[billedVehicleId]

                    if(segHourlyRate < vehicleData.min_rate) {
                        segHourlyRate = vehicleData.min_rate
                    } else if(segHourlyRate > vehicleData.max_rate) {
                        segHourlyRate = vehicleData.max_rate
                    }

                    this.setData(`segments.${i}.hourly_rate`, segHourlyRate)
                }
            }
        }
    }
}

function ValidationErrorMessage(props: any) {
    const count = _.size(props.errors)
    return <div>
        <p>Validation error. Please fix the following {count === 1 ? 'error' : `${count} errors`}:</p>
        <ul>
            {_.flatten(_.values(props.errors)).map((err, i) => <li key={i}>{err}</li>)}
        </ul>
    </div>
}

function toArr<T>(gen: ArrayLike<T> | (() => Generator<T>)): T[] {
    if(_.isFunction(gen)) {
        return Array.from(gen())
    }
    return Array.from(gen)
}
